/**
 * The Bullseye's maths: which ring a person falls in, where their dot goes,
 * and how the score boundaries move. Pure functions, no React and no DOM, so
 * the rules are unit-tested here and the components only draw what this module
 * returns.
 *
 * Ported from the prototype Jared approved on 2026-10-05
 * (https://claude.ai/artifact/1VgSErKWjSSGACc4LxT2Tx). The numbers that shape
 * the picture (the 960x720 canvas, the 252 radius, the 5.5-12.5 cell spacing,
 * the 36 placement retries) are the prototype's, so the tab looks like what
 * was approved. Three departures, all deliberate:
 *
 *   - A dot's seed comes from its review and contact ids, not from its
 *     position in an array. Its spot is stable across renders of the same
 *     lists; switching a list off re-cuts every slice, so dots move then.
 *   - A tie in AI score mode spreads around its score's radius but is clamped
 *     to its own ring. The prototype let a tie near a ring's edge spill up to
 *     9px into the next ring, which put a dot in a band it does not belong to.
 *   - Every dot keeps clear of the white lines drawn over the rings (the ring
 *     separators and the slice dividers): `clearance` turns the widths the
 *     target draws with into the distance a dot's centre keeps from each line
 *     (2026-10-08, Jose: a dot must never sit on a separator). It replaces
 *     the prototype's fixed 7px ring margins and its slice-edge margin.
 *
 * Coordinates are SVG user units in a `0 0 960 720` viewBox, angles are
 * radians with 0 at 3 o'clock, and slices start at 12 o'clock.
 */
import type { BullseyeList, BullseyePerson } from "@nu-assist/contracts";

export const VIEW_W = 960;
export const VIEW_H = 720;
export const CX = 480;
export const CY = 360;
/** Radius of the outer ring. */
export const R = 252;

export const SCORE_STEP = 0.05;
export const MIN_SCORE = 1;
export const MAX_SCORE = 3;
export const MIN_RINGS = 2;
export const MAX_RINGS = 5;
/** The boundaries a search uses until somebody saves its own (sub-task 02). */
export const DEFAULT_BOUNDS: readonly number[] = [2.75, 2.25, 1.75];

export type RingMode = "ai_score" | "criteria_met";

// ---- the widths the target draws with ---------------------------------
// `BullseyeTarget` draws with these and the placement keeps clear of them, so
// a width changed in one place moves the other.

/** Width of the surface-coloured stroke between two rings: thinner past four rings. */
export function separatorWidth(n: number): number {
  return n > 4 ? 3 : 6;
}

/** Width of the surface-coloured line between two slices. */
export const DIVIDER_WIDTH = 4;

/** Dots smaller than this get the thin outline. */
export const THIN_OUTLINE_BELOW = 2.8;

/** Width of a dot's outline. */
export function dotOutline(r: number): number {
  return r < THIN_OUTLINE_BELOW ? 0.9 : 1.3;
}

/** The smallest dot a crowded or thin cell draws. */
export const MIN_DOT_RADIUS = 1.8;

/**
 * How far the centre of a dot of radius `r` stays from the middle of a line
 * `lineWidth` wide: half the line, the dot, half its outline, and 1px of air.
 */
export function clearance(lineWidth: number, r: number): number {
  return lineWidth / 2 + r + dotOutline(r) / 2 + 1;
}

const EPS = 1e-9;
const FULL = 2 * Math.PI - 1e-6;
const START = -Math.PI / 2;

export const f2 = (n: number): string => n.toFixed(2);

/** Round to the 0.05 step, then to two decimals so 2.7 - 0.05 is 2.65, not 2.6500000000000004. */
export function snap(v: number): number {
  return Math.round(Math.round(v / SCORE_STEP) * SCORE_STEP * 100) / 100;
}

/** `k` boundaries spread evenly from 2.75 to 1.75; three rings are the defaults. */
export function evenBounds(k: number): number[] {
  if (k === DEFAULT_BOUNDS.length) return [...DEFAULT_BOUNDS];
  return Array.from({ length: k }, (_, i) => snap(2.75 - (i * 1.0) / (k - 1)));
}

/**
 * Move boundary `i` to `v`, kept in order with a 0.05 gap to its neighbours
 * and inside 1.00-3.00. Returns a new array.
 */
export function clampBound(bounds: readonly number[], i: number, v: number): number[] {
  const hi = i === 0 ? MAX_SCORE : (bounds[i - 1] ?? MAX_SCORE) - SCORE_STEP;
  const lo = i === bounds.length - 1 ? MIN_SCORE : (bounds[i + 1] ?? MIN_SCORE) + SCORE_STEP;
  const next = [...bounds];
  next[i] = snap(Math.min(hi, Math.max(lo, v)));
  return next;
}

/** Criteria a person misses: every criterion the AI did not score 3, unscored ones included. */
export function missingCount(scores: BullseyePerson["scores"]): number {
  return scores.filter((s) => s !== 3).length;
}

export interface RingModel {
  mode: RingMode;
  /** The AI score boundaries (kept in Criteria met mode too, unused there). */
  bounds: readonly number[];
  /** Number of rings drawn. */
  count: number;
  /** The ring a person falls in, or `null` for a non-fit. */
  bandOf: (person: BullseyePerson, list: BullseyeList) => number | null;
  label: (band: number) => string;
  desc: (band: number) => string;
  nonfitDesc: string;
  /** How many rings a list can fill; the rest of its slice is hatched. */
  reach: (list: BullseyeList) => number;
}

/**
 * The ring rule.
 *
 * Criteria met: a person's ring is the number of criteria they miss, and
 * missing all of them is a non-fit. There are as many rings as the active list
 * with the most criteria has.
 *
 * AI score: a person's ring is the first boundary their weighted score
 * reaches; below the last boundary is a non-fit.
 */
export function ringModel(
  mode: RingMode,
  bounds: readonly number[],
  activeLists: readonly BullseyeList[]
): RingModel {
  if (mode === "criteria_met") {
    const count = Math.max(1, ...activeLists.map((l) => l.criteria.length));
    return {
      mode,
      bounds,
      count,
      bandOf: (p, l) => {
        const missing = missingCount(p.scores);
        return missing < l.criteria.length ? missing : null;
      },
      label: (b) => (b === 0 ? "All met" : `Missing ${b}`),
      desc: (b) =>
        b === 0
          ? "Every criterion scored 3"
          : b === 1
            ? "One criterion below 3"
            : `${b} criteria below 3`,
      nonfitDesc: "No criterion scored 3",
      reach: (l) => l.criteria.length,
    };
  }
  const t = bounds;
  const at = (b: number) => t[b] ?? MIN_SCORE;
  return {
    mode,
    bounds: t,
    count: t.length,
    bandOf: (p) => {
      const b = t.findIndex((x) => p.weighted_total >= x - EPS);
      return b < 0 ? null : b;
    },
    label: (b) => `${f2(at(b))}–${f2(b === 0 ? MAX_SCORE : at(b - 1))}`,
    desc: (b) =>
      b === 0
        ? `AI score ${f2(at(0))} or higher`
        : `AI score ${f2(at(b))} to below ${f2(at(b - 1))}`,
    nonfitDesc: `AI score below ${f2(at(t.length - 1))}`,
    reach: () => t.length,
  };
}

/** Outer radius of each ring, centre first. */
export function ringRadii(n: number): number[] {
  return Array.from({ length: n }, (_, b) => (R * (b + 1)) / n);
}

/**
 * The tenure-ramp step (`--tenure-1` lightest … `--tenure-5` strongest) that
 * paints ring `b` of `n`: the strongest tone in the centre, the lightest
 * outside. Five steps, so more than five rings repeat a tone; the surface-
 * coloured stroke between rings still separates them.
 */
export function ringTone(b: number, n: number): 1 | 2 | 3 | 4 | 5 {
  if (n <= 1) return 5;
  return (5 - Math.round((b * 4) / (n - 1))) as 1 | 2 | 3 | 4 | 5;
}

/** Cell (ring x slice) radial limits a dot of ring `b` may use. */
function cellEdges(b: number, radii: readonly number[]): [number, number] {
  return [b === 0 ? 0 : (radii[b - 1] ?? 0), radii[b] ?? R];
}

/**
 * AI score mode: the radius a score sits at inside its ring. A higher score is
 * closer to the centre: the ring's top score sits at `range[0]` and its bottom
 * score at `range[1]`, the radii a dot of the ring may take (`fitDot`).
 */
export function scoreRadius(
  w: number,
  b: number,
  bounds: readonly number[],
  range: readonly [number, number]
): number {
  const hi = b === 0 ? MAX_SCORE : (bounds[b - 1] ?? MAX_SCORE);
  const lo = bounds[b] ?? MIN_SCORE;
  const [rIn, rOut] = range;
  const f = hi === lo ? 0.5 : (hi - w) / (hi - lo);
  return rIn + Math.min(1, Math.max(0, f)) * (rOut - rIn);
}

export interface ScoreSpread {
  /** Radius of the score. */
  center: number;
  /** How far a tie may spread either side of `center`. */
  tolerance: number;
  /** Radius of the central disc a tie at the centre spreads into. */
  disc: number;
  /** True when the score sits so close to the centre that its tie fills a disc instead of a band. */
  inDisc: boolean;
}

/**
 * The order-preserving tie spread for the distinct scores of one ring: each
 * tie spreads at most half-way to the radius of the next distinct score, so
 * the order of scores holds on screen. A tie at the centre (3.00) spreads into
 * a disc reaching 60% of the way to the next score.
 */
export function scoreSpread(
  values: readonly number[],
  b: number,
  bounds: readonly number[],
  radii: readonly number[],
  range: readonly [number, number]
): Map<number, ScoreSpread> {
  const [rIn, rOut] = cellEdges(b, radii);
  const sorted = [...new Set(values)].sort((x, y) => y - x);
  const center = sorted.map((v) => scoreRadius(v, b, bounds, range));
  const out = new Map<number, ScoreSpread>();
  sorted.forEach((v, i) => {
    const r0 = center[i] ?? 0;
    const prev = i > 0 ? Math.abs(r0 - (center[i - 1] ?? r0)) : Number.POSITIVE_INFINITY;
    const nextR = center[i + 1];
    const next = nextR !== undefined ? Math.abs(nextR - r0) : Number.POSITIVE_INFINITY;
    const tolerance = Math.max(2.5, Math.min(16, Math.min(prev, next) / 2, (rOut - rIn) / 2));
    const disc = Math.max(
      tolerance,
      Math.min(
        nextR !== undefined ? (nextR - r0) * 0.6 : Number.POSITIVE_INFINITY,
        (rOut - rIn) * 0.75
      )
    );
    out.set(v, { center: r0, tolerance, disc, inDisc: r0 < tolerance });
  });
  return out;
}

/** Spacing between dots in a cell: wider when the cell is sparse, 5.5 to 12.5. */
export function cellSpacing(span: number, rIn: number, rOut: number, count: number): number {
  const area = Math.PI * (rOut * rOut - rIn * rIn) * (span / (2 * Math.PI));
  return Math.max(5.5, Math.min(12.5, Math.sqrt(area / Math.max(count, 1)) * 0.82));
}

/** Dot radius for a cell's spacing: smaller dots where the cell is crowded. */
export function dotRadius(spacing: number): number {
  return Math.max(MIN_DOT_RADIUS, Math.min(3.9, spacing * 0.32));
}

/**
 * The radius from which a dot can stay `clearance` off both of its slice's
 * dividers, which meet at the centre: nearer the centre the slice is too
 * narrow even at its middle angle. 0 for a lone slice, which has no divider.
 */
export function hubRadius(span: number, r: number, multi: boolean): number {
  if (!multi) return 0;
  return clearance(DIVIDER_WIDTH, r) / Math.sin(Math.min(span, Math.PI) / 2);
}

export interface DotFit {
  /** The dot's radius, at most the one asked for. */
  r: number;
  /** The radii the dot's centre may take. */
  lo: number;
  hi: number;
  /** False when even the smallest dot cannot clear every line (see `fitDot`). */
  fits: boolean;
}

/**
 * The dot size and the radii a dot of ring `b` may use: `clearance` inside
 * the separator on each side (the outer edge `R` counts as one) and outside
 * `hubRadius`, so that at those radii a dot also clears its slice's dividers.
 * A ring too thin for that shrinks the dot first, down to `MIN_DOT_RADIUS`.
 *
 * When even the smallest dot does not fit, the geometry cannot keep it off
 * every line, and every dot of the cell goes to the one point farthest from
 * the nearest line: the slice's middle angle, at the radius where its
 * distance to the dividers equals its distance to the outer separator (or
 * the ring's midline, if that is farther out). It stays in its ring, and
 * comes as close to the lines as the geometry forces, no closer. With the
 * drawing's widths that starts at 27 rings for a lone list, and for
 * L slices once `5.25 / sin(π/L) > 252/n − 4.75` (2 lists: 26+ rings,
 * 3: 24+, 5: 19+, 6: 17+, 10: 12+); `bullseyeGeometry.test.ts` pins both
 * sides of that line.
 */
export function fitDot(
  b: number,
  radii: readonly number[],
  r0: number,
  span: number,
  multi: boolean
): DotFit {
  const [rIn, rOut] = cellEdges(b, radii);
  const sep = separatorWidth(radii.length);
  for (let r = r0; r >= MIN_DOT_RADIUS - EPS; r -= 0.05) {
    const lo = Math.max(b === 0 ? 0 : rIn + clearance(sep, r), hubRadius(span, r, multi));
    const hi = rOut - clearance(sep, r);
    if (lo <= hi) return { r, lo, hi, fits: true };
  }
  // Farthest from the nearest line: the distance to the dividers (k·ρ at the
  // middle angle) and to the inner separator grow with ρ, the distance to the
  // outer one shrinks, so the best ρ is where the slower-growing one meets it.
  const k = multi ? Math.sin(Math.min(span, Math.PI) / 2) : Number.POSITIVE_INFINITY;
  const best = Math.max(b === 0 ? 0 : (rIn + rOut) / 2, rOut / (1 + k));
  return { r: MIN_DOT_RADIUS, lo: best, hi: best, fits: false };
}

/**
 * The angle a dot at radius `rad` keeps from its slice's dividers so it stays
 * `clearance` off them; 0 for a lone slice, which has no divider.
 */
export function dividerMargin(rad: number, r: number, multi: boolean): number {
  if (!multi) return 0;
  return rad <= 0 ? Math.PI / 2 : Math.asin(Math.min(1, clearance(DIVIDER_WIDTH, r) / rad));
}

export interface HistogramBin {
  value: number;
  count: number;
  /** The ring this score falls in, `null` below the last boundary. */
  band: number | null;
}

/**
 * Scores in 0.05 bins from 1.00 to 3.00 (41 bins), each banded by `bounds`.
 * A bin holds the scores from its value up to the next one (floor, not
 * round): boundaries sit on the 0.05 step, so a bin never straddles one and
 * every bar takes the tone of the ring its people are in. (Rounding put a
 * 2.73 in the 2.75 bar, ring 1's tone, while the person is in ring 2.)
 * Scores outside 1-3 are left out.
 */
export function histogram(weights: readonly number[], bounds: readonly number[]): HistogramBin[] {
  const bins = Math.round((MAX_SCORE - MIN_SCORE) / SCORE_STEP);
  const counts = new Array<number>(bins + 1).fill(0);
  for (const w of weights) {
    const i = Math.floor((w - MIN_SCORE) / SCORE_STEP + EPS);
    if (i >= 0 && i <= bins) counts[i] = (counts[i] ?? 0) + 1;
  }
  return counts.map((count, i) => {
    const value = snap(MIN_SCORE + i * SCORE_STEP);
    const b = bounds.findIndex((x) => value >= x - EPS);
    return { value, count, band: b < 0 ? null : b };
  });
}

/** FNV-1a: a stable 32-bit seed from an id. */
export function seedOf(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32: a small seeded generator, so a dot lands in the same spot on every render. */
export function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Vertical position of ring `b`'s label, on the vertical axis above the centre. */
export function labelY(radii: readonly number[], b: number): number {
  return b === 0 ? CY - (radii[0] ?? 0) + 14 : CY - ((radii[b - 1] ?? 0) + (radii[b] ?? 0)) / 2;
}

/** Whether a dot at (x, y) would sit under a ring label. */
export function hitsLabel(x: number, y: number, radii: readonly number[]): boolean {
  return (
    Math.abs(x - CX) < 34 && y < CY && radii.some((_, i) => Math.abs(y - labelY(radii, i)) < 9)
  );
}

/** A list name in lines of at most 22 characters, three lines at most. */
export function wrapName(name: string): string[] {
  const lines: string[] = [];
  for (const word of name.split(/\s+/).filter(Boolean)) {
    const last = lines[lines.length - 1];
    if (last !== undefined && `${last} ${word}`.length <= 22)
      lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  }
  if (lines.length > 3) {
    lines.length = 3;
    lines[2] = (lines[2] ?? "").replace(/.?$/, "…");
  }
  return lines;
}

// ---- paths -------------------------------------------------------------

const pt = (r: number, a: number): [number, number] => [CX + r * Math.cos(a), CY + r * Math.sin(a)];

function circle(r: number): string {
  return `M${CX - r},${CY} a${r},${r} 0 1,0 ${2 * r},0 a${r},${r} 0 1,0 ${-2 * r},0 Z`;
}

/** A pie wedge from the centre, or a whole disc. */
export function wedgePath(a0: number, a1: number, r: number): string {
  if (a1 - a0 >= FULL) return circle(r);
  const [x0, y0] = pt(r, a0);
  const [x1, y1] = pt(r, a1);
  return `M${CX},${CY} L${x0},${y0} A${r},${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1},${y1} Z`;
}

/** An annular sector between radii `r0` and `r1` (a wedge when `r0` is 0). Fill it with `evenodd`. */
export function sectorPath(a0: number, a1: number, r0: number, r1: number): string {
  if (r0 <= 0) return wedgePath(a0, a1, r1);
  if (a1 - a0 >= FULL) return `${circle(r1)} ${circle(r0)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = pt(r1, a0);
  const [x1, y1] = pt(r1, a1);
  const [x2, y2] = pt(r0, a1);
  const [x3, y3] = pt(r0, a0);
  return `M${x0},${y0} A${r1},${r1} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${r0},${r0} 0 ${large} 0 ${x3},${y3} Z`;
}

/** An open arc at radius `r`. */
export function arcPath(a0: number, a1: number, r: number): string {
  const [x0, y0] = pt(r, a0);
  const [x1, y1] = pt(r, a1);
  return `M${x0},${y0} A${r},${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1},${y1}`;
}

// ---- placement ---------------------------------------------------------

/**
 * Points already placed, bucketed on a 16px grid so the "far enough from every
 * other dot" check looks at the neighbouring buckets only. The answer is the
 * same as checking every dot (no spacing exceeds 12.5), just not quadratic:
 * a 1.4k-person search re-lays out on every slider move.
 */
class Taken {
  private readonly grid = new Map<string, [number, number][]>();
  private static readonly CELL = 16;

  private key(gx: number, gy: number): string {
    return `${gx}:${gy}`;
  }

  add(x: number, y: number): void {
    const k = this.key(Math.floor(x / Taken.CELL), Math.floor(y / Taken.CELL));
    const bucket = this.grid.get(k);
    if (bucket) bucket.push([x, y]);
    else this.grid.set(k, [[x, y]]);
  }

  clear(x: number, y: number, minD: number): boolean {
    const gx = Math.floor(x / Taken.CELL);
    const gy = Math.floor(y / Taken.CELL);
    const reach = Math.ceil(minD / Taken.CELL);
    for (let dx = -reach; dx <= reach; dx++) {
      for (let dy = -reach; dy <= reach; dy++) {
        for (const [tx, ty] of this.grid.get(this.key(gx + dx, gy + dy)) ?? []) {
          if ((tx - x) ** 2 + (ty - y) ** 2 <= minD * minD) return false;
        }
      }
    }
    return true;
  }
}

interface SliceGeom {
  a0: number;
  a1: number;
}

/**
 * Up to 36 tries at a free spot: each draws a radius through `radFn` and an
 * angle inside the slice, `margin` off its edges (the slice's middle when the
 * margins meet), and stops at the first one clear of other dots and of the
 * ring labels. When none is clear the last try is used.
 */
function tryPlace(
  next: () => number,
  g: SliceGeom,
  radFn: (v: number) => number,
  margin: (rad: number) => number,
  minD: number,
  taken: Taken,
  radii: readonly number[]
): [number, number] {
  let best: [number, number] = [CX, CY];
  for (let t = 0; t < 36; t++) {
    const u = next();
    const v = next();
    const rad = Math.max(0, radFn(v));
    const marg = margin(rad);
    const ang =
      2 * marg >= g.a1 - g.a0 ? (g.a0 + g.a1) / 2 : g.a0 + marg + u * (g.a1 - g.a0 - 2 * marg);
    best = pt(rad, ang);
    if (!hitsLabel(best[0], best[1], radii) && taken.clear(best[0], best[1], minD)) break;
  }
  taken.add(best[0], best[1]);
  return best;
}

export interface LabelBlock {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

function placeOutside(
  next: () => number,
  g: SliceGeom,
  minD: number,
  taken: Taken,
  blocks: readonly LabelBlock[]
): [number, number] | null {
  for (let t = 0; t < 40; t++) {
    const rad = R + 24 + next() * 76;
    const marg = 8 / rad;
    const ang = g.a0 + marg + next() * (g.a1 - g.a0 - 2 * marg);
    const [x, y] = pt(rad, ang);
    if (x < 8 || x > VIEW_W - 8 || y < 8 || y > VIEW_H - 8) continue;
    if (blocks.some((b) => x > b.x0 - 6 && x < b.x1 + 6 && y > b.y0 - 6 && y < b.y1 + 6)) continue;
    if (taken.clear(x, y, minD)) {
      taken.add(x, y);
      return [x, y];
    }
  }
  return null;
}

export interface SliceLayout {
  listId: string;
  a0: number;
  a1: number;
  /** The slice from the centre to the outer ring: click target and dimming veil. */
  wedge: string;
  /** The coloured arc just outside the slice. */
  arc: string;
  /** The rings this list cannot reach, or `null`. Fill with `evenodd`. */
  hatch: string | null;
  /** End point of the divider on the slice's leading edge. */
  divider: [number, number];
}

export interface ListLabelLayout {
  listId: string;
  x: number;
  y: number;
  anchor: "start" | "middle" | "end";
  lines: string[];
  /** People per ring this list can reach, centre first. */
  counts: number[];
  countsY: number;
}

export interface DotLayout {
  listId: string;
  personId: string;
  x: number;
  y: number;
  r: number;
}

export interface BullseyeLayout {
  radii: number[];
  slices: SliceLayout[];
  labels: ListLabelLayout[];
  labelBlocks: LabelBlock[];
  dots: DotLayout[];
  outsideDots: DotLayout[];
  ringLabels: { text: string; y: number }[];
  /** People per ring across every active list, centre first. */
  ringCounts: number[];
  nonfitTotal: number;
  outsideShown: number;
}

/** Line height of a list label, and the vertical offsets that keep it off its arc. */
const LH = 17;

/**
 * Everything the target draws for the active lists, in slice order. Dots are
 * placed list by list and ring by ring, so the order (and the seeds) decide
 * where a crowded cell's later dots end up, as in the prototype.
 */
export function layoutBullseye(
  lists: readonly BullseyeList[],
  model: RingModel,
  opts: { outside: boolean }
): BullseyeLayout {
  const n = model.count;
  const radii = ringRadii(n);
  const span = lists.length ? (2 * Math.PI) / lists.length : 2 * Math.PI;
  const multi = lists.length > 1;

  const slices: SliceLayout[] = [];
  const labels: ListLabelLayout[] = [];
  const labelBlocks: LabelBlock[] = [];
  const ringCounts = new Array<number>(n).fill(0);

  lists.forEach((l, i) => {
    const a0 = START + i * span;
    const a1 = START + (i + 1) * span;
    const reach = model.reach(l);
    const hatchFrom = reach <= 0 ? 0 : (radii[reach - 1] ?? 0) + separatorWidth(n) / 2;
    const gap = multi ? 0.03 : 0;
    slices.push({
      listId: l.ai_review_id,
      a0,
      a1,
      wedge: wedgePath(a0, a1, R),
      arc: arcPath(a0 + gap, a1 - gap - (multi ? 0 : 0.0001), R + 9),
      hatch: reach < n ? sectorPath(a0, a1, hatchFrom, R) : null,
      divider: pt(R + 1, a0),
    });

    const a = multi ? (a0 + a1) / 2 : START;
    const [x, y] = pt(R + 30, a);
    const c = Math.cos(a);
    const anchor = Math.abs(c) < 0.25 ? "middle" : c > 0 ? "start" : "end";
    const counts = Array.from({ length: Math.max(0, reach) }, () => 0);
    for (const p of l.people) {
      const b = model.bandOf(p, l);
      if (b !== null) {
        counts[b] = (counts[b] ?? 0) + 1;
        ringCounts[b] = (ringCounts[b] ?? 0) + 1;
      }
    }
    const lines = wrapName(l.list_name);
    const top = Math.abs(c) < 0.25 && Math.sin(a) < 0;
    const yOff =
      Math.abs(c) < 0.25
        ? top
          ? -14 - (lines.length - 1) * LH
          : 8
        : -6 - ((lines.length - 1) * LH) / 2;
    labels.push({
      listId: l.ai_review_id,
      x,
      y: y + yOff,
      anchor,
      lines,
      counts,
      countsY: y + yOff + lines.length * LH,
    });
    const w = 180;
    const x0 = anchor === "start" ? x : anchor === "end" ? x - w : x - w / 2;
    labelBlocks.push({ x0, x1: x0 + w, y0: y + yOff - 16, y1: y + yOff + lines.length * LH + 6 });
  });

  const dots: DotLayout[] = [];
  const outsideDots: DotLayout[] = [];
  const taken = new Taken();
  const takenOut = new Taken();
  let nonfitTotal = 0;

  // Every list's people by ring, and the dot each cell's crowding asks for.
  const cells = lists.map((l, i) => {
    const g = slices[i] as SliceLayout;
    const banded = new Map<number, BullseyePerson[]>();
    const nonfit: BullseyePerson[] = [];
    for (const p of l.people) {
      const b = model.bandOf(p, l);
      if (b === null) nonfit.push(p);
      else {
        const band = banded.get(b);
        if (band) band.push(p);
        else banded.set(b, [p]);
      }
    }
    const spacing = new Map<number, number>();
    for (const [b, inBand] of banded) {
      const [rIn, rOut] = cellEdges(b, radii);
      spacing.set(b, cellSpacing(g.a1 - g.a0, rIn, rOut, inBand.length));
    }
    return { l, g, banded, nonfit, spacing };
  });

  // One radial range per ring, fitted for the ring's largest dot: every slice
  // has the same span, so one score sits at one radius in every slice.
  const ringFit = radii.map((_, b) => {
    const asked = cells.flatMap((c) => {
      const minD = c.spacing.get(b);
      return minD === undefined ? [] : [dotRadius(minD)];
    });
    return asked.length ? fitDot(b, radii, Math.max(...asked), span, multi) : null;
  });

  for (const { l, g, banded, nonfit, spacing } of cells) {
    nonfitTotal += nonfit.length;
    const seed = (p: BullseyePerson) => seedOf(`${l.ai_review_id}:${p.import_contact_id}`);

    for (let b = 0; b < n; b++) {
      const inBand = banded.get(b);
      const fit = ringFit[b];
      const minD = spacing.get(b);
      if (!inBand?.length || !fit || minD === undefined) continue;
      const { lo, hi } = fit;
      // A smaller dot than the ring's largest keeps its own size: the range
      // fitted for the larger one clears it too.
      const r = Math.min(dotRadius(minD), fit.r);
      const margin = (rad: number) => dividerMargin(rad, r, multi);
      let place: (p: BullseyePerson) => [number, number];
      if (model.mode === "ai_score") {
        // Scores map onto [lo, hi] and the tie spread is clamped to it, so a
        // tie never spills into a band it does not belong to nor onto a
        // separator. Clamping keeps the order: every score's centre is inside.
        const spread = scoreSpread(
          inBand.map((p) => p.weighted_total),
          b,
          model.bounds,
          radii,
          [lo, hi]
        );
        place = (p) => {
          const s = spread.get(p.weighted_total) as ScoreSpread;
          const radFn = s.inDisc
            ? (v: number) => Math.sqrt(lo * lo + v * Math.max(0, s.disc * s.disc - lo * lo))
            : (v: number) => s.center + (v - 0.5) * 2 * s.tolerance;
          return tryPlace(
            rng(seed(p)),
            g,
            (v) => Math.min(hi, Math.max(lo, radFn(v))),
            margin,
            minD,
            taken,
            radii
          );
        };
      } else {
        // Criteria met: anywhere in [lo, hi], the position carries no meaning.
        place = (p) =>
          tryPlace(
            rng(seed(p)),
            g,
            (v) => Math.sqrt(lo * lo + v * (hi * hi - lo * lo)),
            margin,
            minD,
            taken,
            radii
          );
      }
      for (const p of inBand) {
        const [x, y] = place(p);
        dots.push({ listId: l.ai_review_id, personId: p.import_contact_id, x, y, r });
      }
    }

    if (opts.outside) {
      for (const p of nonfit) {
        const pos = placeOutside(rng(seed(p) + 17), g, 8.5, takenOut, labelBlocks);
        if (pos)
          outsideDots.push({
            listId: l.ai_review_id,
            personId: p.import_contact_id,
            x: pos[0],
            y: pos[1],
            r: 2.5,
          });
      }
    }
  }

  return {
    radii,
    slices,
    labels,
    labelBlocks,
    dots,
    outsideDots,
    ringLabels: lists.length
      ? radii.map((_, b) => ({ text: model.label(b), y: labelY(radii, b) }))
      : [],
    ringCounts,
    nonfitTotal,
    outsideShown: outsideDots.length,
  };
}
